/**
 * No Access Page
 *
 * Page shown to authenticated users who lack admin privileges (their active
 * org does not grant the `platform.user.admin` role).
 * Displays a clear message explaining the situation and provides a sign-out option.
 *
 * @module pages/NoAccessPage
 */

import { Box, Button, Container, Typography } from '@mui/material';
import { TopBar } from '../components/TopBar';
import { useAuthStore } from '../stores/useAuthStore';
import { ROUTES } from '../routes/constants';

/**
 * NoAccessPage - Page for authenticated non-admin users.
 *
 * Features:
 * - TopBar navigation at the top for consistent layout
 * - Centered content area with access denied message
 * - Friendly explanatory text about missing admin privileges
 * - Sign out button to allow logging in with a different account
 *
 * The page is protected by AuthGuard only (no AdminOrStaffGuard), as this is
 * specifically for authenticated users who don't have member-management access.
 */
export function NoAccessPage() {
  const logout = useAuthStore((state) => state.logout);

  /**
   * Handle sign out button click.
   * Calls the logout action with the landing page URL as returnTo,
   * allowing the user to sign in with a different account.
   */
  const handleSignOut = () => {
    const returnTo = `${window.location.origin}${ROUTES.LANDING}`;
    logout(returnTo);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {/* TopBar at the top */}
      <TopBar />

      {/* Centered content area */}
      <Container maxWidth="sm">
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 'calc(100vh - 64px)', // Account for AppBar height
            padding: 3,
            textAlign: 'center',
          }}
          data-testid="no-access-page"
        >
          {/* Access Denied Heading */}
          <Typography
            variant="h4"
            component="h1"
            gutterBottom
            data-testid="no-access-heading"
          >
            Access Denied
          </Typography>

          {/* Explanatory Message */}
          <Typography
            variant="body1"
            data-testid="no-access-message"
            sx={{
              color: 'text.secondary',
              mb: 4,
              maxWidth: 400,
            }}
          >
            You don&apos;t have admin access to this portal. If you believe this
            is an error, please contact your organization administrator.
          </Typography>

          {/* Sign Out Button */}
          <Button
            variant="contained"
            onClick={handleSignOut}
            data-testid="no-access-sign-out-button"
          >
            Sign out
          </Button>
        </Box>
      </Container>
    </Box>
  );
}
